import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Gauge, ChatCircleDots, FileText, FilePlus, Microphone, Briefcase, GraduationCap,
  CurrencyDollar, Trophy, ShieldCheck, CreditCard, Gift, X, Headset,
} from "@phosphor-icons/react";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: Gauge, testId: "m-side-dashboard" },
  { to: "/coach", label: "AI Coach", icon: ChatCircleDots, testId: "m-side-coach" },
  { to: "/resume", label: "Resume Review", icon: FileText, testId: "m-side-resume" },
  { to: "/resume-builder", label: "Resume Builder", icon: FilePlus, testId: "m-side-resume-builder" },
  { to: "/interview", label: "Interview Sim", icon: Microphone, testId: "m-side-interview" },
  { to: "/jobs", label: "Job Matches", icon: Briefcase, testId: "m-side-jobs" },
  { to: "/learning", label: "Learning", icon: GraduationCap, testId: "m-side-learning" },
  { to: "/salary", label: "Salary Tool", icon: CurrencyDollar, testId: "m-side-salary" },
  { to: "/profile", label: "Profile / XP", icon: Trophy, testId: "m-side-profile" },
  { to: "/referrals", label: "Invite & Earn", icon: Gift, testId: "m-side-referrals" },
  { to: "/pricing", label: "Upgrade", icon: CreditCard, testId: "m-side-pricing" },
  { to: "/contact", label: "Contact / Help", icon: Headset, testId: "m-side-contact" },
];

export default function MobileMenu({ open, onClose }) {
  const { user } = useAuth();
  const location = useLocation();

  // Close on route change
  useEffect(() => { if (open) onClose(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [location.pathname]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  return (
    <>
      <div
        onClick={onClose}
        className={`lg:hidden fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        data-testid="mobile-menu-backdrop"
        aria-hidden="true"
      />
      <aside
        data-testid="mobile-menu"
        className={`lg:hidden fixed top-0 right-0 z-[61] h-full w-[80%] max-w-[320px] bg-[#08080A] border-l border-white/10 transform transition-transform duration-200 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 font-semibold">Workspace</div>
          <button
            onClick={onClose}
            data-testid="mobile-menu-close"
            className="-mr-2 w-11 h-11 inline-flex items-center justify-center text-zinc-400 hover:text-white rounded-md"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="flex flex-col gap-1 p-3 overflow-y-auto h-[calc(100%-4rem)]">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              className={({ isActive }) =>
                `min-h-[44px] flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-blue-600/15 text-white border border-blue-500/30"
                    : "text-zinc-300 hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <it.icon size={18} weight="duotone" />
              {it.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              data-testid="m-side-admin"
              className={({ isActive }) =>
                `mt-2 min-h-[44px] flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-amber-600/15 text-white border border-amber-500/30"
                    : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/5 border border-transparent"
                }`
              }
            >
              <ShieldCheck size={18} weight="duotone" /> Admin Panel
            </NavLink>
          )}
        </nav>
      </aside>
    </>
  );
}
